import { useEffect, useRef, useCallback } from 'react';
import { shouldShowNewsletterPrompt } from '../util';
import { trackNewsletterPopupShown } from '../analytics';
import { shouldShowInstallHelperModal } from '../utils/installHelper';
import {
  MONDIALI_OPENING_CEREMONY_OPEN_DELAY_MS,
  isMondialiOpeningCeremonyWindowActive,
  markMondialiOpeningCeremonyModalSeen,
  shouldShowMondialiOpeningCeremonyModal,
} from '../utils/mondialiOpeningCeremony';

export const HOMEPAGE_PROMO_OPENING_CEREMONY = 'mondiali_opening_ceremony';
export const HOMEPAGE_PROMO_INSTALL_HELPER = 'install_helper';
export const HOMEPAGE_PROMO_NEWSLETTER = 'newsletter_signup';

export const shouldWaitForMondialiOpeningCeremonyData = ({
  pathname,
  isProfileLoading,
  nowMs = Date.now(),
}) => (
  pathname === '/'
  && isProfileLoading === true
  && isMondialiOpeningCeremonyWindowActive(nowMs)
);

export const getHomepagePromoCandidate = ({
  pathname,
  mondiali,
  isNewsletterSubscribed = null,
  nowMs = Date.now(),
  userId,
  installHelperEligible = shouldShowInstallHelperModal(),
  newsletterEligible = shouldShowNewsletterPrompt(isNewsletterSubscribed),
}) => {
  if (pathname !== '/') {
    return null;
  }

  if (shouldShowMondialiOpeningCeremonyModal({ pathname, mondiali, nowMs, userId })) {
    return HOMEPAGE_PROMO_OPENING_CEREMONY;
  }

  if (installHelperEligible) {
    return HOMEPAGE_PROMO_INSTALL_HELPER;
  }

  if (newsletterEligible) {
    return HOMEPAGE_PROMO_NEWSLETTER;
  }

  return null;
};

export const getHomepagePromoDelayMs = (promoKey) => (
  promoKey === HOMEPAGE_PROMO_OPENING_CEREMONY
    ? MONDIALI_OPENING_CEREMONY_OPEN_DELAY_MS
    : 0
);

export const useHomepagePromoScheduler = ({
  pathname,
  homepageEntryId,
  isGlobalModalOpen,
  isProfileLoading,
  mondiali,
  isNewsletterSubscribed,
  userId,
  openMondialiOpeningCeremony,
  openInstallHelper,
  openNewsletter,
}) => {
  const timerRef = useRef(null);
  const handledHomepageEntryRef = useRef(null);
  const effectiveNewsletterSubscription = isProfileLoading ? null : isNewsletterSubscribed;

  const clearPendingTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    clearPendingTimer();
  }, [clearPendingTimer]);

  useEffect(() => {
    if (pathname !== '/') {
      handledHomepageEntryRef.current = null;
      clearPendingTimer();
      return;
    }

    if (handledHomepageEntryRef.current !== homepageEntryId) {
      clearPendingTimer();
    }
  }, [pathname, homepageEntryId, clearPendingTimer]);

  useEffect(() => {
    if (pathname !== '/') {
      return;
    }

    if (handledHomepageEntryRef.current === homepageEntryId) {
      return;
    }

    if (isGlobalModalOpen) {
      clearPendingTimer();
      return;
    }

    const nowMs = Date.now();
    if (shouldWaitForMondialiOpeningCeremonyData({ pathname, isProfileLoading, nowMs })) {
      clearPendingTimer();
      return;
    }

    const candidate = getHomepagePromoCandidate({
      pathname,
      mondiali,
      isNewsletterSubscribed: effectiveNewsletterSubscription,
      nowMs,
      userId,
    });

    if (!candidate) {
      handledHomepageEntryRef.current = homepageEntryId;
      clearPendingTimer();
      return;
    }

    clearPendingTimer();
    timerRef.current = setTimeout(() => {
      handledHomepageEntryRef.current = homepageEntryId;

      if (candidate === HOMEPAGE_PROMO_OPENING_CEREMONY) {
        markMondialiOpeningCeremonyModalSeen(userId);
        openMondialiOpeningCeremony?.();
        return;
      }

      if (candidate === HOMEPAGE_PROMO_INSTALL_HELPER) {
        openInstallHelper?.();
        return;
      }

      if (candidate === HOMEPAGE_PROMO_NEWSLETTER) {
        openNewsletter?.();
        trackNewsletterPopupShown();
      }
    }, getHomepagePromoDelayMs(candidate));

    return clearPendingTimer;
  }, [
    pathname,
    homepageEntryId,
    isGlobalModalOpen,
    isProfileLoading,
    mondiali,
    isNewsletterSubscribed,
    effectiveNewsletterSubscription,
    userId,
    openMondialiOpeningCeremony,
    openInstallHelper,
    openNewsletter,
    clearPendingTimer,
  ]);
};
